import {until} from 'lit-html/directives/until';
import {repeat} from 'lit-html/directives/repeat';
import{LoadingEllipses} from '@epg/epg-components';
import {productLabels, products as aoflProducts, productLogos, productKeepGrayScale} from '../constants-enumerate';
// This has to be here for dynamic styling!!! (otherwise the css linter
// will clean the dynamic styles out :'(
const dynamicCss = ['.product-logo', '.abcmouse', '.mathmastery', '.readingmastery', '.readingiq', '.abcmouse_english'];
import {generalLabels} from '@modules/i18n-labels';
import {wrapUnsafeHtml} from '@modules/wrap-unsafe-html';

const renderEstimate = (ctx, html, product, license) => {
  if (ctx.estimating[license.id]) {
    return html`<loading-ellipses></loading-ellipses>`;
  }

  if (ctx.selectedLicenseInfo[product] !== license.id) {
    return '';
  }

  const total = ctx.estimates[license.id].total_license_cost || 0;

  if (total === 0) {
    return html`
      <div class="text-danger">
        <strong>${until(ctx.__('<tt-VEz6Z72S>', 'No students to license'))}</strong>
      </div>`;
  }

  if (ctx.errors[license.id]) {
    return html`
      <div class="text-danger">
        ${total} (<strong>${until(ctx.__('<tt-73j4Niry>', 'You don\'t have enough licenses'))}</strong>)
      </div>`;
  }

  return html`${total}`;
};

const renderLicenseRow = (ctx, html, product, license) => {
  if (license.expired) {
    return '';
  }
  return html`
<div class="card cursor-pointer shadow-sm mb-3 ${ctx.errors[license.id] ? 'card-danger' : ''}" @click=${() => {
  ctx.toggle(product, license.id);
}}
  @keypress="${(e) => (e.key === 'Enter') ? ctx.toggle(product, license.id): null}"
  >
  <div class="card-body license-card ${ctx.selectedLicenseInfo[product] === license.id ? 'card-selected' : ''}">
    <div class="row no-gutters">
      <div class="col-xl-10 col-lg-9 col-md-12 col-sm-12 col-xs-12 p-4">
        <div class="row no-gutters">
          <div class="col-xl-6 col-lg-12 col-md-12 col-sm-12 col-xs-12">
            <div class="text-muted">${until(ctx.__('<tt-nKtIvU0R>', 'License Name'))}</div>
            <div><strong>${license.license_name}</strong></div>
          </div>
          <div class="col-xl-3 col-lg-12 col-md-12 col-sm-12 col-xs-12">
            <div class="text-muted">${until(ctx.__('<tt-JhHbO6IQ>', 'License Type'))}</div>
            <div class="font-size-bigger text-wrap">${until((license.expiration_type === 'duration') ? generalLabels.DurationLabel : generalLabels.FixedEndDateLabel)}</div>
            <div class="text-muted">${until(generalLabels.TransferableLicense)}</div>
            <div class="font-size-bigger text-wrap">${license.transferable ? until(generalLabels.Yes) : until(generalLabels.No)}</div>
          </div>
          <div class="col-xl-3 col-lg-12 col-md-12 col-sm-12 col-xs-12">
            <div class="text-muted">${until(ctx.__('<tt-ogr34PvN>', 'Home Access Type'))}</div>
            <div>${until(wrapUnsafeHtml(license.home_access_type))}</div>
            ${license.fspk ? html`
            <div class="text-muted">${until(ctx.__('<tt-mO4yEiYW>', 'FSPK'))}</div>
            <div>${until(generalLabels.Yes)}</div>
            ` : ''}
          </div>
        </div>
        <div class="row no-gutters">
          <div class="col-xl-3 col-lg-12 col-md-6 col-sm-12 col-xs-12">
            <div class="text-muted">${until(ctx.__('<tt-NM9S3QX3>', 'Total #'))}</div>
            <div>${license.total}</div>
          </div>
          <div class="col-xl-3 col-lg-12 col-md-6 col-sm-12 col-xs-12">
            <div class="text-muted">${until(ctx.__('<tt-7d5zQYyW>', 'Available #'))}</div>
            <div>${license.available}</div>
          </div>
          <div class="col-xl-3 col-lg-12 col-md-6 col-sm-12 col-xs-12">
            <div class="text-muted">${until(ctx.__('<tt-6pZb39AC>', 'Licenses to be Used'))}</div>
            <div>${renderEstimate(ctx, html, product, license)}</div>
          </div>
          ${license.test ? html`
          <div class="col-xl-3 col-lg-12 col-md-6 col-sm-12 col-xs-12">
            <div class="text-muted">${until(ctx.__('<tt-ds3BJr03>', 'Test'))}</div>
            <div>${until(generalLabels.Yes)}</div>
          </div>` : ''}
        </div>
      </div>
      <div class="col-2 col-xl-2 col-lg-3 col-md-12 col-sm-12 col-xs-12 p-4 divider-vertical ${ctx.selectedLicenseInfo[product] === license.id ? 'selected' : ''}">
        <span tabindex="0" class="radio ${ctx.selectedLicenseInfo[product] === license.id ? 'selected' : ''}"></span> ${until(ctx.__('<tt-71ehRTt4>', 'Select'))}
      </div>
    </div>
  </div>
</div>
`;
};

const renderSection = (ctx, html, product, content) => html`
  <div class="license-logo-container pl-2 mb-3 d-flex align-items-center">
    <img src="${productLogos[product].icon.active}"
         class="ml-2 ${productKeepGrayScale.includes(product) ? 'black-img' : ''}"
         width="${productLogos[product].icon.width}"
         height="${productLogos[product].icon.height}"
         alt="${productLabels[product]}"/>
  </div>

  ${content}
`;

const renderLicenseSection = (ctx, html, product) => {
  if (!ctx.aoflProducts[product]) {
    return '';
  }

  let content;

  const products = ctx.licensesByProduct[product] || [];
  if (products.length > 0) {
    content = html`
        <div class="text-nowrap mb-5">
          ${repeat(ctx.licensesByProduct[product] || [], (license) => license.id, (license) => renderLicenseRow(ctx, html, product, license))}
        </div>
    `;
  } else {
    content = renderNoLicensesMessage(ctx, html);
  }

  return renderSection(ctx, html, product, content);
};

const renderNoLicensesMessage = (ctx, html) => html`
  <p class="text-center text-muted p-5">${until(ctx.__('<tt-NMUGcJHg>', 'You do not have any licenses to assign yet. To request product licenses, please contact your admin.'))}</p>
`;

const renderLicenseAssignFooter = (ctx, html) => html`
<div class="row w-100 justify-content-center pb-5">
  <div class="text-danger mb-sm-3">${(ctx.disableAssign && ctx.showErrorMsg) ? until(ctx.__('<tt-xFTKCUfN>', 'One or more selected licenses do not have enough available inventory to assign to all selected students, or the selected students already have the license and cannot receive it again. Please deselect them and choose different licenses.')) : ''}</div>
  <div class="col-sm-6 col-md-6 col-lg-4 col-xl-4">
    <button
      @click=${ctx.confirmation}
      class="btn btn-primary rounded-pill btn-block ${ctx.disableAssign ? 'disabled' : ''}"
      type="submit"
      ?disabled=${ctx.disableAssign}
    >
      <div class="${ctx.uploading ? 'spinner-border' : ''} mr-3">
      </div>${until(ctx.__('<tt-B2yN1wy2>', 'Assign Licenses'))}
    </button>
  </div>
</div>
`;

const renderLicenseSelection = (ctx, html) => html`
  ${!ctx.orgHasProducts ? renderNoLicensesMessage(ctx, html) : ''}
  ${renderLicenseSection(ctx, html, aoflProducts.MY_MATH_ACADEMY)}
  ${renderLicenseSection(ctx, html, aoflProducts.MY_READING_ACADEMY)}
  ${renderLicenseSection(ctx, html, aoflProducts.MY_READING_ACADEMY_ES)}
  ${renderLicenseSection(ctx, html, aoflProducts.MATH_ACADEMY)}
  ${renderLicenseSection(ctx, html, aoflProducts.ABCMOUSE)}
  ${renderLicenseSection(ctx, html, aoflProducts.ABCMOUSE_ENGLISH)}
  ${renderLicenseSection(ctx, html, aoflProducts.READINGIQ)}
  ${renderLicenseAssignFooter(ctx, html)}
`;

const renderConfirmation = (ctx, html) => html`
  <div class="modal-header flex-column">
    <h5 class="mb-0">${until(ctx.__('<tt-FF6k4vu8>', 'Confirmation'))}</h5>
  </div>

  <div class="modal-body pt-0">
    ${getConfirmMessage(ctx, html)}
    <p class="mt-5">${until(ctx.__('<tt-uivExs37>', 'Continue?'))}</p>
  </div>

  <div class="modal-footer">
    <div class="row w-100 justify-content-center">
      <div class="col-sm-6 col-md-6 col-lg-4 col-xl-4">
        <button
          @click=${ctx.back}
          class="btn btn-outline-primary rounded-pill btn-block"
          type="submit">
          ${until(ctx.__('<tt-o4DN7a83>', 'Back'))}
        </button>
      </div>
      <div class="col-sm-6 col-md-6 col-lg-4 col-xl-4">
        <button
          @click=${ctx.assignLicenses}
          class="btn btn-primary rounded-pill btn-block"
          type="submit">

          <div class="${ctx.uploading ? 'spinner-border' : ''} mr-3">
          </div>${until(ctx.__('<tt-Ve8uLVR0>', 'Continue Assigning'))}
        </button>
      </div>
    </div>
  </div>
`;

const getConfirmMessage = (ctx, html) => {
  if (ctx.objectType === 'student' && ctx.objectIds?.length === 1 && Object.keys(ctx.conflictCount).length === 0) {
    return Object.keys(ctx.selectedLicenseInfo)
      .map((product) => {
        let content;

        const licenseId = ctx.selectedLicenseInfo[product];

        const result = ctx.studentLicenseResults.licenses[licenseId];
        if (result.status === 'unassigned' || result.status === 'expired') {
          return '';
        }

        if (result.compatible) {
          content = html`
            <p>${until(ctx.__('<tt-87RABElK>', 'This student already has an active license with the same details as the new one you want to assign. You can still use your available license to update it (i.e. extend its duration or update the end date).'))}</p>
            <p>${until(ctx.__('<tt-cgr/prRC>', 'If you do not wish to update the existing license, we recommend you revoke the license before assigning a new one.'))}</p>
          `;
        } else {
          content = html`
            <p>${until(ctx.__('<tt-sn3dGSoz>', 'This student already has a license for this product, but the license details are different. Assigning this new license will fully replace the existing license, including its duration/expiration.'))}</p>
            <p>${until(ctx.__('<tt-Rh0M6nFP>', 'If you do not wish to overwrite the current license, we recommend you wait until it expires before assigning a new one.'))}</p>
          `;
        }

        return renderSection(ctx, html, product, content);
      });
  } else if (Object.keys(ctx.conflictCount).length > 0) {
    return Object.keys(ctx.selectedLicenseInfo)
      .map((product) => {
        const licenseId = ctx.selectedLicenseInfo[product];

        return html`
          <p>${until(ctx._r(
            ctx._c('<tt-tRTxgimN>',
              '%c1% already have access to %r2% through a different provider and they cannot receive a redeem code for home access.',
              {1: '1 student', '%other%': '%r1% students'},
              ctx.conflictCount[licenseId]
            ),
            ctx.conflictCount[licenseId], productLabels[product]))}</p>
        `;
    });
  }

  return html`
    <p>${until(ctx.__('<tt-5QfWvb/5>', 'Some of your selected students already have an active license. Assigning a new one will use your available license inventory and cause one of the following changes:'))}</p>
    <ul>
      <li>${until(ctx.__('<tt-Tke2Dg2L>', 'If the new license details are same as the current except for the duration or end date, the new license will simply update (i.e. extend its duration or update the end date)'))}</li>
      <li>${until(ctx.__('<tt-mkd/RAPI>', 'If the new license details are different than the current license, the old license will be replaced by the new one.'))}</li>
    </ul>
    <p>${until(ctx.__('<tt-fptZ/5q8>', 'Note: If you do not wish to update or replace the current license, we recommend you revoke or wait until the existing licenses expire before assigning a new one.'))}</p>
  `;
};


export default (ctx, html) => {
  return ctx.screen === 'selection' ? renderLicenseSelection(ctx, html) : renderConfirmation(ctx, html);
};
